import { Box, Button as MuiButton, Link, Stack } from "@mui/material";
import { Button, FormBox, MRTGrid } from "@foodpilot/foods";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NoSuppliers } from "./NoSuppliers";
import { createMRTColumnHelper } from "material-react-table";
import { CompanyModal } from "./CompanyModal/CompanyModal";
import { GridText } from "src/components/Grid/GridText";
import {
    IngredientSupplierTransportControl,
    IngredientSupplierTransportDialog,
    IngredientSupplierTransportTableCell,
} from "src/components/Transport/IngredientSupplierTransportFields";
import {
    IngredientFormIngredientCompany,
    IngredientFormIngredientReferenceLine,
    IngredientSupplierTransportSelection,
    IngredientFormType,
} from "../../IngredientForm.types";
import { useAllIngredientReferences } from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import { getSupplierMutationPermission } from "./supplierMutationPermission";
import type { IngredientSupplierTransportChangeReason } from "src/components/Transport/ingredientSupplierTransportTypes";
import { getNextIngredientSupplierTransportStatus } from "src/components/Transport/ingredientSupplierTransportPersistence";
import { IngredientSupplierReferenceField } from "./IngredientSupplierReferenceField";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

type Page2Props = {
    ingredient: IngredientFormType;
    setIngredient: (newIngredient: IngredientFormType) => void;
};

export const Page2 = (props: Page2Props) => {
    const { ingredient, setIngredient } = props;
    const [isOpen, setOpen] = useState(false);
    const openModal = () => setOpen(true);
    const canModify = ingredient.canModify !== false;

    const setSupplierIngredientReference = (
        supplierIri: IRI | undefined,
        reference: IngredientFormIngredientReferenceLine | null,
    ) => {
        // Index by either the persisted `@id` (existing supplier) or by the
        // supplier's IRI (newly added, not-yet-saved suppliers don't have a row id).
        const next = ingredient.suppliers.map((row) => {
            const matches =
                supplierIri !== undefined && (row["@id"] === supplierIri || row.supplier["@id"] === supplierIri);
            if (!matches) return row;
            return { ...row, ingredientReference: reference };
        });
        setIngredient({ ...ingredient, suppliers: next });
    };

    const setSupplierTransportSelections = (
        supplierIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => {
        setIngredient({
            ...ingredient,
            suppliers: ingredient.suppliers.map((row) =>
                row.supplier["@id"] === supplierIri ?
                    {
                        ...row,
                        transportSelections,
                        transportSelectionsStatus: getNextIngredientSupplierTransportStatus(
                            row.transportSelectionsStatus,
                            reason,
                        ),
                    }
                :   row,
            ),
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "2rem",
            }}
        >
            <CompanyModal
                canModify={canModify}
                existingIngredientSuppliers={ingredient.suppliers}
                isOpen={isOpen}
                setOpen={setOpen}
                onValidate={(newIngredientSuppliers) => {
                    setIngredient({
                        ...ingredient,
                        suppliers: newIngredientSuppliers,
                    });
                }}
            />
            {ingredient.suppliers.length > 0 ?
                <Fragment>
                    <DisplayCompanies
                        companies={ingredient.suppliers}
                        canModify={canModify}
                        openModal={openModal}
                        onChangeIngredientReference={setSupplierIngredientReference}
                        onChangeTransportSelections={setSupplierTransportSelections}
                    />
                </Fragment>
            : canModify ?
                <NoSuppliers openModal={openModal} />
            :   null}
        </div>
    );
};

type DisplayCompaniesProps = {
    companies: IngredientFormIngredientCompany[];
    canModify?: boolean;
    openModal: () => void;
    onChangeIngredientReference: (
        supplierIri: IRI | undefined,
        reference: IngredientFormIngredientReferenceLine | null,
    ) => void;
    onChangeTransportSelections: (
        supplierIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => void;
};
export const DisplayCompanies = (props: DisplayCompaniesProps) => {
    const { companies, canModify = true, openModal, onChangeIngredientReference, onChangeTransportSelections } = props;
    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();
    const [transportRow, setTransportRow] = useState<IngredientFormIngredientCompany | null>(null);
    const getTransportSelections = (row: IngredientFormIngredientCompany): IngredientSupplierTransportSelection => {
        return row.transportSelections ?? {};
    };

    // The list endpoint already filters down to references the current tenant can
    // see (platform-owned + own-tenant), so no extra client-side filtering is needed.
    const { ingredientReferences, isLoading: isLoadingReferences } = useAllIngredientReferences({
        itemsPerPage: 200,
    });

    const referenceOptions = useMemo<IngredientFormIngredientReferenceLine[]>(
        () =>
            (ingredientReferences ?? []).map((ref) => ({
                "@id": ref["@id"],
                id: ref.id,
                name: ref.name,
            })),
        [ingredientReferences],
    );

    const columnHelper = createMRTColumnHelper<IngredientFormIngredientCompany>();
    const isEditable = getSupplierMutationPermission(canModify) === "allowed";
    const mrt_columns = [
        columnHelper.accessor((row) => row.supplier.name, {
            enableSorting: false,
            id: "name",
            header: t("nom"),
            Cell: ({ renderedCellValue }) =>
                isProductUxV2 ?
                    <GridText value={renderedCellValue} variant="themed" />
                :   <Link sx={{ color: "inherit", textDecoration: "none" }}>{renderedCellValue}</Link>,
        }),
        columnHelper.accessor((row) => row.supplier.clientId, {
            id: "clientId",
            header: t("code_fournisseur"),
            Cell: (value) => <GridText value={value.renderedCellValue} />,
        }),
        columnHelper.accessor((row) => row.ingredientReference?.name ?? "", {
            id: "ingredientReference",
            header: t("ingredient.form.suppliers.ingredientReferenceColumn", "Ingredient reference (override)"),
            enableSorting: false,
            Cell: ({ row }) => {
                const supplierRow = row.original;
                const rowKey = supplierRow["@id"] ?? supplierRow.supplier["@id"];
                const canEdit = isEditable;

                return (
                    <IngredientSupplierReferenceField
                        appearance={isProductUxV2 ? "product-v2" : "legacy"}
                        disabled={!canEdit}
                        isLoading={isLoadingReferences}
                        options={referenceOptions}
                        value={supplierRow.ingredientReference ?? null}
                        onChange={(newValue) => {
                            if (canEdit) onChangeIngredientReference(rowKey, newValue);
                        }}
                    />
                );
            },
        }),
        columnHelper.display({
            id: "transport",
            header: t("transport", "Transport"),
            enableSorting: false,
            Cell: ({ row }) => {
                const transportSelections = getTransportSelections(row.original);
                const canEdit = isEditable;

                const TransportControl =
                    isProductUxV2 ? IngredientSupplierTransportControl : IngredientSupplierTransportTableCell;

                return (
                    <TransportControl
                        appearance={isProductUxV2 ? "product-v2" : "legacy"}
                        disabled={!canEdit}
                        ingredientSupplierId={extractIdFromIri(row.original["@id"])}
                        value={transportSelections}
                        onConfigure={() => {
                            if (canEdit) setTransportRow(row.original);
                        }}
                    />
                );
            },
        }),
    ];

    return (
        <Box style={{ display: "flex", gap: "2rem" }}>
            <FormBox sx={{ flex: 3 }}>
                <MRTGrid enableSorting={false} enableRowSelection={false} columns={mrt_columns} data={companies} />
                {transportRow !== null && (
                    <IngredientSupplierTransportDialog
                        appearance={isProductUxV2 ? "product-v2" : "legacy"}
                        open
                        onClose={() => setTransportRow(null)}
                        title={transportRow.supplier.name}
                        ingredientSupplierId={extractIdFromIri(transportRow["@id"])}
                        value={getTransportSelections(transportRow)}
                        onChange={(next, reason) =>
                            onChangeTransportSelections(transportRow.supplier["@id"], next, reason)
                        }
                    />
                )}
                {!isEditable ?
                    null
                : isProductUxV2 ?
                    <Stack alignItems="flex-start" sx={{ p: 2 }}>
                        <Button variant="text" onClick={openModal}>
                            {t("ingredient.form.suppliermodal.title")}
                        </Button>
                    </Stack>
                :   <MuiButton variant="text" onClick={openModal}>
                        {t("ingredient.form.suppliermodal.title")}
                    </MuiButton>
                }
            </FormBox>
        </Box>
    );
};

const extractIdFromIri = (iri?: IRI): number | undefined => {
    if (iri === undefined) {
        return undefined;
    }

    const rawId = iri.split("/").pop();
    const id = rawId === undefined ? NaN : Number.parseInt(rawId, 10);

    return Number.isNaN(id) ? undefined : id;
};
